/**
 * Lexical relevance — no deps, deterministic, edge-safe. Ranks catalog entries (skills,
 * commands, memories, docs) by a task hint so a LARGE catalog doesn't dump every entry into
 * the prompt. TF-IDF weighted: rare terms in the corpus score higher than common ones.
 */

const STOP = new Set([
  'the', 'and', 'for', 'with', 'that', 'this', 'from', 'into', 'your', 'you', 'are', 'was', 'will',
  'use', 'using', 'run', 'add', 'fix', 'make', 'file', 'files', 'code', 'please', 'need', 'want', 'should', 'all',
]);

/** Lowercased alphanumeric tokens of length ≥ 3, minus a few stopwords. */
export function tokenize(s: string): Set<string> {
  const out = new Set<string>();
  for (const w of String(s ?? '').toLowerCase().match(/[a-z0-9]+/g) ?? []) {
    if (w.length >= 3 && !STOP.has(w)) out.add(w);
  }
  return out;
}

/** Build IDF weights from a corpus of texts. Rare terms get higher weight. */
export function idfWeights(corpus: string[]): Map<string, number> {
  const N = corpus.length;
  if (N === 0) return new Map();
  const df = new Map<string, number>();
  for (const doc of corpus) for (const t of tokenize(doc)) df.set(t, (df.get(t) ?? 0) + 1);
  const idf = new Map<string, number>();
  for (const [t, n] of df) idf.set(t, Math.log((N + 1) / (n + 1)) + 1); // smooth IDF
  return idf;
}

/** TF-IDF weighted relevance score. Without IDF, falls back to flat token overlap (backward-compatible). */
export function relevanceScore(text: string, queryTokens: Set<string>, idf?: Map<string, number>): number {
  if (queryTokens.size === 0) return 0;
  const t = tokenize(text);
  let score = 0;
  for (const q of queryTokens) if (t.has(q)) score += idf?.get(q) ?? 1;
  return score;
}

/**
 * Split `items` into the `k` most relevant to `query` (by TF-IDF overlap with `text(item)`)
 * and the rest, preserving each item's original order within its group. Returns everything in
 * `kept` (empty `rest`) when `items.length <= k` or `query` is blank — so it's a no-op for
 * small lists. When `corpus` is provided, builds IDF weights for better ranking.
 */
export function topByRelevance<T>(items: T[], query: string, text: (x: T) => string, k: number, corpus?: string[]): { kept: T[]; rest: T[] } {
  if (!Number.isInteger(k) || k < 1) return { kept: items, rest: [] }; // invalid k → no-op (keep all), never hide
  if (items.length <= k || !query.trim()) return { kept: items, rest: [] };
  const q = tokenize(query);
  if (q.size === 0) return { kept: items.slice(0, k), rest: items.slice(k) };
  const idf = corpus?.length ? idfWeights(corpus) : undefined;
  const scored = items.map((x, i) => ({ i, s: relevanceScore(text(x), q, idf) }));
  scored.sort((a, b) => b.s - a.s || a.i - b.i); // best score first; stable by original index
  const keep = new Set(scored.slice(0, k).map((e) => e.i));
  return { kept: items.filter((_, i) => keep.has(i)), rest: items.filter((_, i) => !keep.has(i)) };
}
